import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { exportDcsCsv, exportDcsExcel, exportDcsPdf } from "@/lib/dcExport";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Download, FileDown, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";

const STATUS_OPTIONS = [
  { value: "all", label: "All" },
  { value: "ready", label: "Ready" },
  { value: "dispatched", label: "Dispatched" },
];

// Local-calendar YYYY-MM-DD (not toISOString, which is UTC).
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// "Export DCs" on the Delivery Challans page: filter by created date and
// status, then download one row per DC line as CSV / Excel / PDF.
export default function DcExportPanel() {
  const today = new Date();
  const [open, setOpen] = useState(false);
  const [fromDate, setFromDate] = useState(isoDay(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate, setToDate] = useState(isoDay(today));
  const [statuses, setStatuses] = useState(["all"]);
  const [busy, setBusy] = useState("");

  const toggleStatus = (value, checked) => {
    if (value === "all") { setStatuses(["all"]); return; }
    const next = checked ? [...statuses.filter((s) => s !== "all"), value] : statuses.filter((s) => s !== value);
    setStatuses(next.length ? next : ["all"]);
  };

  const statusLabel = statuses.includes("all")
    ? "All"
    : STATUS_OPTIONS.filter((o) => statuses.includes(o.value)).map((o) => o.label).join(", ");

  const run = async (format) => {
    if (!fromDate || !toDate) { toast.error("Pick both From Date and To Date"); return; }
    if (fromDate > toDate) { toast.error("From Date is after To Date"); return; }
    setBusy(format);
    try {
      const { data } = await api.get("/delivery-challans/export-lines", {
        params: { from_date: fromDate, to_date: toDate, status: statuses.join(",") },
      });
      const rows = data.rows || [];
      if (!rows.length) { toast.info("No delivery challans match these filters"); return; }
      if (format === "csv") exportDcsCsv(rows, fromDate, toDate);
      else if (format === "xlsx") exportDcsExcel(rows, fromDate, toDate);
      else exportDcsPdf(rows, fromDate, toDate, statusLabel);
      toast.success(`Exported ${data.dc_count} DC(s), ${rows.length} line(s)`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Export failed");
    } finally { setBusy(""); }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" data-testid="dc-export-dcs-btn"><Download className="w-4 h-4 mr-1.5" />Export DCs</Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 space-y-4" data-testid="dc-export-panel">
        <div className="text-sm font-semibold text-slate-800">Export Delivery Challans</div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">From Date</Label>
            <Input type="date" value={fromDate} max={toDate || undefined} onChange={(e) => setFromDate(e.target.value)} className="h-9" data-testid="dc-export-from" />
          </div>
          <div>
            <Label className="text-xs">To Date</Label>
            <Input type="date" value={toDate} min={fromDate || undefined} onChange={(e) => setToDate(e.target.value)} className="h-9" data-testid="dc-export-to" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Status</Label>
          <div className="mt-1.5 flex gap-4" data-testid="dc-export-status">
            {STATUS_OPTIONS.map((o) => (
              <label key={o.value} className="flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer">
                <Checkbox checked={statuses.includes(o.value)} onCheckedChange={(v) => toggleStatus(o.value, !!v)} data-testid={`dc-export-status-${o.value}`} />
                {o.label}
              </label>
            ))}
          </div>
        </div>
        <div className="grid gap-2 pt-1">
          {[
            { format: "csv", label: "Export CSV", Icon: FileText },
            { format: "xlsx", label: "Export Excel", Icon: FileSpreadsheet },
            { format: "pdf", label: "Export PDF", Icon: FileDown },
          ].map(({ format, label, Icon }) => (
            <Button key={format} size="sm" variant="outline" className="justify-start" disabled={!!busy} onClick={() => run(format)} data-testid={`dc-export-${format}`}>
              <Icon className="w-4 h-4 mr-1.5" />{busy === format ? "Exporting…" : label}
            </Button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
